import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { verifyPayment, apiError } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle, Coins } from "lucide-react";

const Shell = ({ children }) => (
    <div className="min-h-screen bg-obsidian text-white flex items-center justify-center px-4" data-testid="payment-callback-page">
        <div className="w-full max-w-md border border-white/10 bg-surface p-8 text-center">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blaze mb-6">
                SPCLIVE // Payment
            </div>
            {children}
        </div>
    </div>
);

const ActionLinks = () => (
    <div className="flex gap-2 justify-center mt-6">
        <Link
            to="/console/stream"
            className="px-5 py-2.5 bg-blaze hover:bg-blazeHover text-white font-mono uppercase text-[11px] tracking-[0.2em] transition-colors"
            data-testid="callback-go-stream"
        >
            Start Streaming
        </Link>
        <Link
            to="/console/billing"
            className="px-5 py-2.5 border border-white/20 hover:border-white/50 text-white/70 font-mono uppercase text-[11px] tracking-[0.2em] transition-colors"
            data-testid="callback-go-billing"
        >
            Billing
        </Link>
    </div>
);

const VerifyingScreen = () => (
    <>
        <Loader2 className="w-8 h-8 text-blaze animate-spin mx-auto" />
        <p className="font-mono text-xs text-white/60 mt-4" data-testid="callback-verifying">
            Confirming your payment with Flutterwave...
        </p>
    </>
);

const SuccessScreen = ({ data }) => (
    <>
        <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto" />
        <h1 className="font-display font-bold uppercase text-2xl mt-4">Payment Confirmed</h1>
        <p className="font-mono text-xs text-white/60 mt-3" data-testid="callback-success">
            +{(data.credits_added || 0).toLocaleString()} credits added.
        </p>
        <p className="font-mono text-sm mt-2 flex items-center justify-center gap-2 text-white/90">
            <Coins className="w-4 h-4 text-blaze" />
            New balance: <span className="font-bold">{(data.credits || 0).toLocaleString()}</span>
        </p>
        <ActionLinks />
    </>
);

const CancelledScreen = () => (
    <>
        <XCircle className="w-10 h-10 text-white/40 mx-auto" />
        <h1 className="font-display font-bold uppercase text-2xl mt-4">Payment Cancelled</h1>
        <p className="font-mono text-xs text-white/60 mt-3" data-testid="callback-cancelled">
            No charge was made. You can try again anytime.
        </p>
        <ActionLinks />
    </>
);

const UnauthScreen = () => (
    <>
        <XCircle className="w-10 h-10 text-blaze mx-auto" />
        <h1 className="font-display font-bold uppercase text-2xl mt-4">Session Expired</h1>
        <p className="font-mono text-xs text-white/60 mt-3" data-testid="callback-unauth">
            Please log in — if you were charged, your credits will be added automatically.
        </p>
        <Link
            to="/login"
            className="inline-block mt-6 px-5 py-2.5 bg-blaze hover:bg-blazeHover text-white font-mono uppercase text-[11px] tracking-[0.2em] transition-colors"
            data-testid="callback-go-login"
        >
            Log In
        </Link>
    </>
);

const FailedScreen = ({ phase, message }) => (
    <>
        <XCircle className="w-10 h-10 text-blaze mx-auto" />
        <h1 className="font-display font-bold uppercase text-2xl mt-4">
            {phase === "invalid" ? "Invalid Payment Link" : "Payment Not Completed"}
        </h1>
        <p className="font-mono text-xs text-white/60 mt-3" data-testid="callback-failed">
            {message || "This payment could not be confirmed. If you were charged, credits will be added automatically — or contact support."}
        </p>
        <ActionLinks />
    </>
);

const CallbackBody = ({ state }) => {
    if (state.phase === "verifying") return <VerifyingScreen />;
    if (state.phase === "success") return <SuccessScreen data={state.data} />;
    if (state.phase === "cancelled") return <CancelledScreen />;
    if (state.phase === "unauthenticated") return <UnauthScreen />;
    return <FailedScreen phase={state.phase} message={state.message} />;
};

function verifyFromUrl(setState) {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    const txRef = params.get("tx_ref");
    const transactionId = params.get("transaction_id");
    if (status === "cancelled") {
        setState({ phase: "cancelled" });
        return;
    }
    if (!txRef || !transactionId || !txRef.startsWith("spclive-")) {
        setState({ phase: "invalid" });
        return;
    }
    verifyPayment(Number(transactionId), txRef)
        .then((d) => setState({ phase: "success", data: d }))
        .catch((e) => {
            if (e.response?.status === 401) setState({ phase: "unauthenticated" });
            else setState({ phase: "failed", message: apiError(e) });
        });
}

export default function PaymentCallback() {
    const [state, setState] = useState({ phase: "verifying" });

    useEffect(() => {
        verifyFromUrl(setState);
    }, []);

    return (
        <Shell>
            <CallbackBody state={state} />
        </Shell>
    );
}
